import React, { useEffect, useRef, useState } from 'react';
import { BOOT_ESCAPE_HATCH_MS } from '../lib/bootDeadline';
import {
  BOOT_SURFACE_FORCE_DISMISS_MS,
  emitBootSurfaceForcedDismissBeacon,
  emitBootSurfacePhaseBeacon,
  noteBootSurfaceAlbumCount,
  noteBootSurfaceDaemonReady,
  resolveBootSurfaceStatus,
  subscribeBootSurfaceStatus,
} from '../lib/bootSurfaceStatus';

const GOLD = '#C9A84C';

/**
 * Cold-boot loading shell — same surface / same dismiss owner as today.
 * Adds time-driven status copy + opportunistic signal upgrades + 30s force belt.
 * Escape hatch "Sign in" visible from second 3 (#126).
 */
export default function BootLoadingScreen({
  onSignInNow,
  signInLabel = 'Sign in',
  onForceDismiss,
}) {
  const [showEscape, setShowEscape] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [signalRev, setSignalRev] = useState(0);
  const [forcedOff, setForcedOff] = useState(false);
  const forcedRef = useRef(false);
  // Date.now so vitest fake timers (and real clocks) both advance elapsed.
  const originRef = useRef(Date.now());

  useEffect(() => {
    const timer = window.setTimeout(() => setShowEscape(true), BOOT_ESCAPE_HATCH_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => subscribeBootSurfaceStatus(() => {
    setSignalRev((n) => n + 1);
  }), []);

  useEffect(() => {
    // Event-only upgrades — never import IPC gates here (async resolve after
    // jsdom teardown threw ReferenceError: window is not defined in suite runs).
    const onAlbums = (event) => {
      const count = event?.detail?.count ?? event?.detail?.albumCount;
      if (count != null) noteBootSurfaceAlbumCount(count);
    };
    const onCatalogReady = () => noteBootSurfaceDaemonReady(true);
    window.addEventListener('maestro:boot-album-progress', onAlbums);
    window.addEventListener('maestro:local-catalog-ready', onCatalogReady);
    return () => {
      window.removeEventListener('maestro:boot-album-progress', onAlbums);
      window.removeEventListener('maestro:local-catalog-ready', onCatalogReady);
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      setElapsedMs(Date.now() - originRef.current);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (forcedRef.current) return;
    if (elapsedMs < BOOT_SURFACE_FORCE_DISMISS_MS) return;
    forcedRef.current = true;
    setForcedOff(true);
    emitBootSurfaceForcedDismissBeacon(elapsedMs);
    onForceDismiss?.();
  }, [elapsedMs, onForceDismiss]);

  void signalRev;
  const status = resolveBootSurfaceStatus(elapsedMs);

  useEffect(() => {
    if (forcedOff) return;
    emitBootSurfacePhaseBeacon(status.phase, {
      source: status.source,
      album_count: status.albumCount,
    });
  }, [status.phase, status.source, status.albumCount, forcedOff]);

  if (forcedOff) return null;

  return (
    <div
      data-testid="boot-loading-screen"
      style={{
        minHeight: '100vh',
        background: 'var(--bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        padding: 24,
      }}
    >
      <div
        data-testid="boot-progress-spinner"
        style={{
          width: 48,
          height: 48,
          border: '3px solid rgba(201, 168, 76, 0.2)',
          borderTopColor: GOLD,
          borderRadius: '50%',
          animation: 'boot-spin 0.8s linear infinite',
        }}
      />
      <p
        data-testid="boot-progress-message"
        data-phase={status.phase}
        data-source={status.source}
        style={{
          margin: 0,
          color: 'rgba(255, 255, 255, 0.82)',
          fontSize: 15,
          fontWeight: 600,
          textAlign: 'center',
          lineHeight: 1.45,
          maxWidth: 420,
        }}
      >
        {status.message}
      </p>
      {showEscape && (
        <button
          type="button"
          data-testid="boot-sign-in-escape"
          onClick={onSignInNow}
          style={{
            padding: '12px 28px',
            borderRadius: 12,
            border: `1px solid ${GOLD}`,
            background: `${GOLD}22`,
            color: 'var(--text-primary, #fff)',
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {signInLabel}
        </button>
      )}
      <style>{`@keyframes boot-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
